import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Package,
  Warehouse,
  Truck,
  CheckCircle2,
  XCircle,
  Undo2,
} from "lucide-react";
import DateRangeFilter from "../../components/dateRangeFilter/DateRangeFilter";
import CustomTable from "../../components/customTable";
import HoldingFilterModal from "../../components/filterModal/holdingFilterModal";
import useAuth from "../../hooks/useAuth";
import { getHoldingPackages, getHoldingMe } from "../../api/holding";
import { getPackagesByStatusCount } from "../../api/packages";
import { packageStatus, packageStatusStyles } from "../../utils/packageStatus";
import { showToast } from "../../utils/showToast";
import { apiBodyMessage } from "../../utils/apiErrorMessage";
import { deliveredFromResume } from "../../utils/packagesResume";
import { ErrorState } from "../../components/dashboard/shared/DashboardShared";
import { SummaryCardsSkeleton } from "../../components/ui/SummaryCardsSkeleton";
import { TableSkeleton } from "../../components/ui/TableSkeleton";
import EvidenceView from "../../components/evidenceView";
import moment from "moment";
import Spinner from "../../components/spinner/spinner";

// Max length of the search field, per search type. Mirrors the backend DTO
// (microservice-packages filter-packages.dto): followingNumber and order_id are
// @MaxLength(64), shipping_address.address_line is @MaxLength(255). No minimum:
// a single-digit correlativo or a short order id is a valid search.
const QUERY_MAX_LENGTH = {
  followingNumber: 64,
  order_id: 64,
  "shipping_address.address_line": 255,
};

/**
 * Clon de `views/adminView/resume/resume.jsx` para el perfil "admin de holding".
 * Diferencias funcionales:
 *  - Consume `getHoldingPackages` (vía `/packages`, microservice-packages aplica
 *    scope holding desde el JWT).
 *  - Usa `HoldingFilterModal` (sección "Cliente" con Vendedor + Sender ID, sin
 *    "Asignación" ni "Discrepancia MercadoLibre").
 *  - El filtro `vendorCompanyId` del modal se mappea a `companyId` para que
 *    microservice-packages restrinja por empresa hija específica.
 *  - KPIs por estado: reusa `getPackagesByStatusCount` con
 *    `holding.logisticCompanyId`. NOTA: microservice-reports debe agregar
 *    filtro por holding scope para que los conteos reflejen solo las hijas
 *    (TODO server-side).
 */
export default function HoldingResume({ update, resetUpdate }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const holding = getHoldingMe();
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [inStorage, setInStorage] = useState(0);
  const [picked, setPicked] = useState(0);
  const [inWarehouse, setInWarehouse] = useState(0);
  const [inRoute, setInRoute] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [pendingReturns, setPendingReturns] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [updateData, setUpdateData] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const [isRequestComplete, setIsRequestComplete] = useState(false);
  // Backend text when the load fails: without it a failed request looked the
  // same as "no results" (empty table).
  const [loadError, setLoadError] = useState(null);
  const [viewEvidence, setViewEvidence] = useState(false);
  const [packageEvidence, setPackageEvidence] = useState({});
  const [query, setQuery] = useState({ type: "", query: "" });
  const [filters, setFilters] = useState({});
  const [showResume, setShowResume] = useState({
    startDate: moment().subtract(6, "days").startOf("day").format("MM/DD/YYYY"),
    endDate: moment().endOf("day").format("MM/DD/YYYY"),
  });
  const [datePreset, setDatePreset] = useState("7d");
  const [updateCards, setUpdateCards] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  const { height } = window.visualViewport || {};
  const totalDocsPerPage = Math.floor(((height || window.innerHeight || 800) * 0.53) / 55 - 2);
  const docsPerPage = Math.max(totalDocsPerPage || 6, 20);
  const logisticCompanyId = holding?.logisticCompanyId || user?.logisticCompanyId || null;

  const loadData = useCallback(
    async (page = 1, reset = false, isInitialLoad = false) => {
      if (!user?.id) return;

      if (reset) {
        setLoadingTable(true);
        if (isInitialLoad) setLoading(true);
        setLoadError(null);
        setIsRequestComplete(false);
        setData([]);
        setTableData([]);
        setCurrentPage(1);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      setUpdateData(false);
      resetUpdate(false);

      const dateStartFromFilters = filters?.dateStart
        ? moment(filters.dateStart, "YYYY-MM-DD").format("MM/DD/YYYY")
        : undefined;

      // endDateCreation is already inclusive (end of day) in the backend: no +1 day.
      const dateEndFromFilters = filters?.dateEnd
        ? moment(filters.dateEnd, "YYYY-MM-DD").format("MM/DD/YYYY")
        : undefined;

      const hasDateFilters = Boolean(dateStartFromFilters || dateEndFromFilters);
      const isBlankSearch = query.query === "";

      const startDateParam =
        !hasDateFilters && isBlankSearch ? showResume.startDate : dateStartFromFilters || undefined;
      const endDateParam =
        !hasDateFilters && isBlankSearch ? showResume.endDate : dateEndFromFilters || undefined;
      const updatedAtSinceParam = !hasDateFilters && isBlankSearch ? showResume.startDate : undefined;

      try {
        const response = await getHoldingPackages({
          limit: docsPerPage,
          page,
          query,
          sortType: "desc",
          orderField: filters?.orderBy || "updatedAt",
          coms: filters?.coms,
          zipCode: filters?.zipCode,
          status: filters?.status,
          startDate: startDateParam,
          endDate: endDateParam,
          companyId: filters?.vendorCompanyId,
          updatedAtSince: updatedAtSinceParam,
        });

        // `getHoldingPackages` never throws: it resolves `{success:false, error}`.
        // Without this guard a failed request fell through to the "no docs"
        // branch and the table simply looked empty.
        if (!response?.success) {
          // The wrapper falls back to a plain Spanish string when the request
          // never reached the server (no response body). Only a real HTTP body
          // goes through apiBodyMessage; anything else gets the translated text.
          const body = response?.error;
          const message =
            (typeof body === "object" && body !== null ? apiBodyMessage(body) : null) ??
            t("No se pudieron cargar los paquetes");
          showToast(message, false);
          if (reset) {
            setLoadError(message);
            setData([]);
            setTotalDocs(0);
            setIsRequestComplete(true);
          } else {
            // Stop infinite scroll from retrying a failing page forever.
            setHasMore(false);
          }
          return;
        }

        if (response?.data?.docs) {
          const result = response.data.docs;
          setTotalDocs(response.data.totalDocs);

          if (reset) {
            setData(result);
            setIsRequestComplete(true);
          } else {
            setData((prev) => [...prev, ...result]);
          }

          const totalPages = Math.ceil((response.data.totalDocs || 0) / docsPerPage);
          setHasMore(page < totalPages);
        } else {
          if (reset) {
            setData([]);
            setTotalDocs(0);
            setIsRequestComplete(true);
          }
        }
      } catch (error) {
        console.error("Error fetching holding packages:", error);
        showToast(t("Error de servidor"), false);
        if (reset) {
          setData([]);
          setTotalDocs(0);
          setIsRequestComplete(true);
          setLoadingTable(false);
          if (isInitialLoad) setLoading(false);
        }
      } finally {
        if (!reset) setLoadingMore(false);
      }
    },
    [user?.id, docsPerPage, query, filters, showResume, resetUpdate, t],
  );

  useEffect(() => {
    if (user?.id) {
      const isInitialLoad = isInitialLoadRef.current;
      if (isInitialLoad) {
        setLoading(true);
        setLoadingTable(true);
        setHasStartedLoading(true);
        isInitialLoadRef.current = false;
      } else {
        setLoadingTable(true);
      }
      setTableData([]);
      loadData(1, true, isInitialLoad);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, updateData, update, query, filters, showResume]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || !user?.id) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadData(nextPage, false);
  }, [loadingMore, hasMore, currentPage, user?.id, loadData]);

  useEffect(() => {
    if (observerRef.current && observerTarget.current) {
      observerRef.current.unobserve(observerTarget.current);
      observerRef.current = null;
    }
    if (!hasMore || loadingMore || loadingTable || !data.length || !user?.id) return;
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loadingMore && !loadingTable && user?.id) {
          loadMore();
        }
      },
      { root: null, rootMargin: "300px", threshold: 0.01 },
    );
    observer.observe(currentTarget);
    observerRef.current = observer;

    return () => {
      if (observerRef.current && currentTarget) {
        observerRef.current.unobserve(currentTarget);
        observerRef.current = null;
      }
    };
  }, [hasMore, loadingMore, loadingTable, user?.id, loadMore, data.length]);

  useEffect(() => {
    if (updateCards && logisticCompanyId) {
      getPackagesByStatusCount({
        logisticCompanyId,
        startDate: moment(showResume.startDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
        endDate: moment(showResume.endDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      })
        .then((data) => {
          setPicked(data.status1 || 0);
          setInWarehouse(data.status2 || 0);
          setInRoute(data.status3_30);
          setDelivered(deliveredFromResume(data));
          setInStorage(data.status6_7);
          setPendingReturns(data.status21_22 || 0);
        })
        .catch((error) => {
          console.error("Error en getPackagesByStatusCount:", error);
        })
        .finally(() => setUpdateCards(false));
    }
  }, [logisticCompanyId, showResume, updateCards]);

  useEffect(() => {
    if (loadingTable) setTableData([]);
  }, [loadingTable]);

  useEffect(() => {
    if (!hasStartedLoading || !user?.id) return;
    if (loadingTable && !data) return;

    if (loadingTable && Array.isArray(data) && data.length === 0 && hasStartedLoading && isRequestComplete) {
      setTableData([]);
      setLoadingTable(false);
      if (loading) setLoading(false);
      return;
    }
    if (loadingTable && Array.isArray(data) && data.length === 0 && !isRequestComplete) return;

    if (data && data.length > 0) {
      const parsed = data.map((element) => ({
        correlative: {
          value: (
            <div className="custom-center-inner-td ml-3">
              <span>{element?.followingNumber}</span>
            </div>
          ),
          type: "correlative",
          id: element._id,
          comparative: element?.followingNumber,
        },
        sale: (
          <div className="custom-center-inner-td">
            {element?.senderUser?.senderBusinessName ||
              element?.senderInfo?.businessName ||
              t("Sin información")}
          </div>
        ),
        client: (
          <div className="d-grid">
            <span>{element?.receiver_name}</span>
            <span>{element?.receiver_phone}</span>
          </div>
        ),
        destiny: (
          <div className="d-grid">
            <span>
              {element?.shipping_address?.street_number != null
                ? `${element?.shipping_address?.street_name}, ${element?.shipping_address?.street_number}`
                : element?.shipping_address?.address_line}
            </span>
            <span className="custom-table-city-name">
              {element?.shipping_address?.municipality?.name}, {element?.shipping_address?.city?.name}
            </span>
          </div>
        ),
        comment: (
          <div className="d-grid">
            <span>{element?.shipping_address?.comment || element?.comments || t("Sin comentarios")}</span>
          </div>
        ),
        deliveryMan: {
          name: element?.assignTo
            ? `${element?.assignTo?.name} ${element?.assignTo?.lastName}`
            : t("No asignado"),
          icon: element?.assignTo?.avatar,
        },
        business: element?.senderInfo?.businessName || t("Sin información"),
        lastUpdate: (
          <div className="custom-center-inner-td">
            {moment(element?.updatedAt).format("DD/MM/YY HH:mm")}
          </div>
        ),
        createdAt: (
          <div className="custom-center-inner-td">
            {moment(element?.createdAt).format("DD/MM/YY HH:mm")}
          </div>
        ),
        status: {
          statusPill: (
            <div className="custom-center-inner-td">
              <div className={`${packageStatusStyles[element?.status]}`}>
                {packageStatus[element?.status]}
              </div>
            </div>
          ),
          value: element?.status,
          status: element?.status,
        },
        orderId: element?.order_id,
        packageType: element?.type,
        incidentId: element?.incidentId || null,
        incidentDate: element?.incidentDate || null,
        incidentResolvedAt: element?.incidentResolvedAt || null,
      }));
      setTableData(parsed);
      setLoadingTable(false);
      if (loading) setLoading(false);
    } else if (
      data &&
      Array.isArray(data) &&
      data.length === 0 &&
      hasStartedLoading &&
      user?.id &&
      isRequestComplete
    ) {
      if (loadingTable) {
        setTableData([]);
        setLoadingTable(false);
        if (loading) setLoading(false);
      } else {
        setTableData([]);
      }
    }
  }, [data, loadingTable, loading, hasStartedLoading, user?.id, isRequestComplete, t]);

  // KPIs por estado = prefiltros tonales. El tono (ícono + barra + borde activo)
  // usa el MISMO token de estado que el pill de fila (paridad pill↔card):
  // Recolectados/En bodega → info, En ruta → warning, Entregados → success,
  // Entrega fallida → error, Devoluciones → reverse.
  const totalCards = [
    { title: t("Recolectados"), icon: Package, tone: "info", value: picked, filter: { status: [1] } },
    { title: t("En bodega"), icon: Warehouse, tone: "info", value: inWarehouse, filter: { status: [2] } },
    { title: t("En ruta"), icon: Truck, tone: "warning", value: inRoute, filter: { status: [3, 30, 31, 32] } },
    { title: t("Entregados"), icon: CheckCircle2, tone: "success", value: delivered, filter: { status: [4] } },
    { title: t("Entrega fallida"), icon: XCircle, tone: "error", value: inStorage, filter: { status: [6, 7] } },
    { title: t("Devoluciones pendientes"), icon: Undo2, tone: "reverse", value: pendingReturns, filter: { status: [21, 22] } },
  ];

  // `delivered` cuenta por fecha de entrega y es null si el backend no pudo
  // contarlo: en ese caso no suma.
  const cardsTotal =
    picked + inWarehouse + inRoute + (delivered ?? 0) + inStorage + pendingReturns;

  const handleCardClick = (clickedCard) => {
    totalCards.forEach((c) => (c.selected = false));
    if (selectedCard?.title === clickedCard.title) {
      setSelectedCard(null);
      setFilters({});
      return;
    }
    setCurrentPage(1);
    setSelectedCard(clickedCard);
    setFilters({
      ...clickedCard.filter,
      dateStart: moment(showResume.startDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      dateEnd: moment(showResume.endDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
    });
  };

  // Cambio de rango desde el DateRangeFilter (preset rápido o personalizado).
  // Recibe fechas "YYYY-MM-DD"; actualiza el scope (showResume → recalcula
  // KPIs y tabla) y, si hay una card de estado activa, su rango de filtros.
  const handleRangeChange = (startYMD, endYMD, preset) => {
    setDatePreset(preset);
    setShowResume({
      startDate: moment(startYMD).format("MM/DD/YYYY"),
      endDate: moment(endYMD).format("MM/DD/YYYY"),
    });
    setUpdateCards(true);
    setCurrentPage(1);
    if (selectedCard) {
      setFilters((prev) => ({ ...prev, dateStart: startYMD, dateEnd: endYMD }));
    }
  };

  const headers = [
    t("Correlativo"),
    t("Vendedor"),
    t("Cliente"),
    t("Dirección de entrega"),
    t("Comentario"),
    t("Repartidor"),
    t("Última actualización"),
    t("Creado"),
    t("Estado"),
    " ",
  ];

  const [unappliedQuery, setUnappliedQuery] = useState("");

  const searchButtonAction = () => {
    // `unappliedQuery` starts as "" (string), so never read `.query` directly.
    const text = (unappliedQuery?.query ?? "").trim();
    // This view sends no `q`: it sends the typed field, so the limit is the one
    // that field declares in the backend DTO — followingNumber / order_id up to
    // 64 chars, shipping_address.address_line up to 255 — with no minimum.
    const queryType = unappliedQuery?.type || "followingNumber";
    // Unknown type (a search option added later without a bound here) falls back
    // to the loosest limit: wrongly blocking a valid search is the bug this
    // guard exists to fix, and an over-long value still surfaces the backend
    // error through the table's error state.
    const maxLength =
      QUERY_MAX_LENGTH[queryType] ?? QUERY_MAX_LENGTH["shipping_address.address_line"];
    if (text.length > maxLength) {
      showToast(
        t("La búsqueda no puede superar los {{max}} caracteres.", { max: maxLength }),
        "warning"
      );
      return;
    }
    setCurrentPage(1);
    if (text === "") {
      setQuery({ query: "", type: "" });
      return;
    }
    setQuery({ ...unappliedQuery, query: text });
  };

  const searchValue = (q, queryType) => {
    setUnappliedQuery({ type: queryType, query: q });
  };

  if (loading) {
    // Skeleton inicial acorde al layout real (header + KPI cards + tabla).
    return (
      <div className="w-100 all-orders-container mt-6" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <div className="orders-header-container">
          <h2 className="orders-header-container__title">{t("Todos los pedidos")}</h2>
        </div>
        <SummaryCardsSkeleton count={totalCards.length} />
        <div className="all-orders-container__table">
          {/* +1 por la columna de checkbox que CustomTable antepone a los headers. */}
          <TableSkeleton rows={Math.min(docsPerPage || 8, 10)} columns={headers.length + 1} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-100 all-orders-container mt-6">
      {viewEvidence ? (
        <EvidenceView
          selectedPackage={packageEvidence}
          completePackages={data}
          setViewEvidence={setViewEvidence}
          // Editar o cancelar desde el detalle deja la lista vieja: se recarga
          // con el mismo disparador que usan los paneles del menú de cada fila.
          onPackageChanged={() => setUpdateData(true)}
        />
      ) : (
        <>
          <div className="orders-header-container">
            <h2 className="orders-header-container__title">{t("Todos los pedidos")}</h2>
            <div className="summary-cards-header__date-range">
              <DateRangeFilter value={datePreset} onChange={handleRangeChange} />
            </div>
          </div>

          <div className="summary-cards-container">
            {totalCards.map((item, index) => {
              const Icon = item.icon;
              const active = selectedCard?.title === item.title;
              const pct =
                cardsTotal > 0 ? Math.round((item.value / cardsTotal) * 100) : 0;
              return (
                <button
                  key={index}
                  type="button"
                  className={`summary-card summary-card--${item.tone}${
                    active ? " summary-card--active" : ""
                  }`}
                  // La tarjeta sigue respondiendo durante la carga de la tabla: con
                  // `pointer-events` apagado el clic no hacía nada y Clarity lo
                  // contaba como clic muerto (la mayoría de los usuarios de escritorio
                  // busca y hace clic en una tarjeta mientras la tabla recarga).
                  onClick={() => handleCardClick(item)}
                >
                  <div className="summary-card__top">
                    <span className="summary-card__icon" aria-hidden="true">
                      <Icon size={18} strokeWidth={2} />
                    </span>
                    <span className="summary-card__value">
                      {item.value == null ? "—" : item.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="summary-card__label">{item.title}</div>
                  <div className="summary-card__bar">
                    <div
                      className="summary-card__bar-fill"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="all-orders-container__table" id="table-container">
            <CustomTable
              title={""}
              headers={headers}
              data={tableData}
              totalDocs={totalDocs}
              page={null}
              actions={true}
              completeData={data}
              updateData={setUpdateData}
              docsPerPage={docsPerPage}
              loadingTable={loadingTable}
              pageSelected={null}
              setViewEvidence={setViewEvidence}
              setPackageEvidence={setPackageEvidence}
              filters={filters}
              setFilters={(value) => {
                setSelectedCard(null);
                setFilters(value);
              }}
              admin={true}
              infiniteScroll={true}
              showSearchBar={!viewEvidence}
              searchBarProps={
                !viewEvidence
                  ? {
                      onChange: searchValue,
                      loading: loadingTable,
                      searchButton: true,
                      onSearchButton: searchButtonAction,
                    }
                  : null
              }
              FilterModalComponent={HoldingFilterModal}
              emptyState={
                loadError ? (
                  <ErrorState message={loadError} onRetry={() => loadData(1, true)} />
                ) : undefined
              }
            />
          </div>

          {data.length > 0 && !loadingTable && (
            <>
              {hasMore && (
                <div ref={observerTarget} className="infinite-scroll-observer">
                  {loadingMore && (
                    <div className="infinite-scroll-loading">
                      <Spinner />
                      <span className="ml-2">{t("Cargando más pedidos...")}</span>
                    </div>
                  )}
                </div>
              )}
              {!hasMore && (
                <div className="infinite-scroll-end">
                  <span>{t("No hay más pedidos para mostrar")}</span>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
